import { Controller } from "@hotwired/stimulus"
import { ColorType, createChart, createSeriesMarkers, LineSeries, LineStyle } from "lightweight-charts"
import { buildChartOptions, buildMaSeriesOptions, buildStageBands, CHART_COLORS } from "../utils/chart_utils"
import { TrendGaugeBand } from "../utils/trend_gauge_band"
import { ACTIVE_BG, INACTIVE_BG } from "../utils/power_trend_background"

// Draws one lab study result: the index line, its SMA, in/out background
// shading and entry/exit markers.
//
// The chart normally lives in a Bootstrap tab pane that is display:none on
// connect (clientWidth 0), which lightweight-charts cannot size. So render()
// is deferred to the tab's `shown.bs.tab` event and is idempotent — the
// controller element must wrap BOTH the tab list and the pane so the action
// bubbles to it. If the pane happens to already be visible (deep link to the
// tab), connect() renders straight away.
//
//   div data-controller="lab-study-chart" \
//       data-lab-study-chart-chart-data-url-value=(lab_study_chart_data_path(study)) \
//       data-lab-study-chart-variant-value="band"
//     ul.nav.nav-tabs role="tablist"
//       li.nav-item role="presentation"
//         button.nav-link#chart-tab type="button" role="tab" \
//           data-bs-toggle="tab" data-bs-target="#chart-pane" \
//           data-action="shown.bs.tab->lab-study-chart#render"
//           | Chart
//     .tab-content
//       .tab-pane#chart-pane role="tabpanel"
//         .btn-group.btn-group-sm.mb-2
//           - %w[band cross].each_with_index do |variant, i|
//             button.btn.btn-outline-secondary type="button" \
//               class=("active" if i.zero?) \
//               data-lab-study-chart-target="variantButton" \
//               data-variant=variant \
//               data-lab-study-chart-variant-param=variant \
//               data-action="lab-study-chart#showVariant"
//               = variant.titleize
//         div data-lab-study-chart-target="chart" style="height: 420px"

const INDEX_LINE_WIDTH = 2
const SMA_COLOR = "#f59e0b"
const ENTRY_COLOR = "#16a34a"
const EXIT_COLOR = "#dc2626"
const MAX_LABELLED_MARKERS = 60

export default class extends Controller {
  static targets = ["chart", "variantButton"]
  static values = { chartDataUrl: String, variant: { type: String, default: "band" } }

  connect() {
    this.rendered = false
    this.loadGeneration = 0
    this.onThemeChanged = () => this.applyChartTheme()
    document.addEventListener("theme:changed", this.onThemeChanged)
    if (this.hasChartTarget && this.chartTarget.clientWidth > 0) this.render()
  }

  disconnect() {
    document.removeEventListener("theme:changed", this.onThemeChanged)
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.teardownChart()
  }

  // Idempotent: safe to call on every shown.bs.tab.
  render() {
    if (this.rendered || !this.hasChartTarget || !this.hasChartDataUrlValue) return
    this.rendered = true
    return this.load()
  }

  showVariant(event) {
    const variant = event.params?.variant || event.currentTarget?.dataset?.variant
    if (!variant) return
    this.variantButtonTargets.forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.variant === variant)
    })
    if (variant === this.variantValue) return
    this.variantValue = variant // triggers variantValueChanged
  }

  variantValueChanged() {
    if (this.rendered) this.load()
    // A failed load clears `rendered`; toggling a variant on a visible pane is
    // then the retry, without waiting for the tab to be shown again.
    else if (this.hasChartTarget && this.chartTarget.clientWidth > 0) this.render()
  }

  // Every call takes a generation number: toggling variants faster than the
  // network answers would otherwise let the FIRST response land last and draw
  // a series the button group no longer says is showing. A failure rolls
  // `rendered` back so re-opening the tab retries rather than staying stuck on
  // the error text until a full page reload.
  async load() {
    const generation = ++this.loadGeneration
    const url = `${this.chartDataUrlValue}?variant=${encodeURIComponent(this.variantValue)}`
    let payload
    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      payload = await response.json()
    } catch {
      if (generation !== this.loadGeneration) return
      this.rendered = false
      if (this.element.isConnected && this.hasChartTarget) {
        this.teardownChart()
        this.chartTarget.textContent = "Could not load chart"
      }
      return
    }
    if (generation !== this.loadGeneration) return
    if (!this.element.isConnected || !this.hasChartTarget) return
    this.draw(payload)
  }

  draw(payload) {
    const data = payload?.data || []
    this.teardownChart()
    this.chartTarget.textContent = ""
    if (data.length === 0) {
      this.chartTarget.textContent = "Could not load chart"
      return
    }

    const width = this.chartTarget.clientWidth || 800
    const height = this.chartTarget.clientHeight || 420
    this.chart = createChart(this.chartTarget, buildChartOptions({ width, height, logScale: true, bottomMargin: 0.1 }))
    // The shared options assume a few years of candles (minBarSpacing 5px);
    // this is 26 years of daily closes, and fitContent() cannot go below the
    // minimum, so relax it or the chart opens zoomed into the last year.
    this.chart.applyOptions({ timeScale: { minBarSpacing: 0.05, rightOffset: 2 } })

    this.series = this.chart.addSeries(LineSeries, {
      color: CHART_COLORS.up,
      lineWidth: INDEX_LINE_WIDTH,
      priceScaleId: "right",
      priceLineVisible: false,
      lastValueVisible: false
    })
    const seriesData = data.map((row) => ({ time: row.time, value: row.close }))
    this.series.setData(seriesData)

    try {
      const sma = payload.sma || []
      if (sma.length) {
        const line = this.chart.addSeries(LineSeries, {
          ...buildMaSeriesOptions({ color: SMA_COLOR, lineWidth: 2 }),
          lineStyle: LineStyle.Dashed
        })
        line.setData(sma)
      }
    } catch {
      // A chart without the SMA overlay is still worth showing.
    }

    try {
      const position = payload.position || []
      const bands = buildStageBands(position, (stage) => (stage === "in" ? ACTIVE_BG : INACTIVE_BG))
      // The primitive indexes bars off the SAME array the series was set with.
      if (bands.length) this.series.attachPrimitive(new TrendGaugeBand({ bands, data: seriesData }))
    } catch {
      // A chart without the in/out shading is still worth showing.
    }

    try {
      const raw = payload.markers || []
      // The plain rule switches 200+ times; labels on every arrow become a
      // smear, so keep the arrows and drop the text once there are many.
      const labelled = raw.length <= MAX_LABELLED_MARKERS
      const markers = raw
        .map((m) => (m.side === "entry"
          ? { time: m.time, position: "belowBar", color: ENTRY_COLOR, shape: "arrowUp", text: labelled ? "In" : "", size: 1 }
          : { time: m.time, position: "aboveBar", color: EXIT_COLOR, shape: "arrowDown", text: labelled ? "Cash" : "", size: 1 }))
        .sort((a, b) => String(a.time).localeCompare(String(b.time)))
      if (markers.length) this.markers = createSeriesMarkers(this.series, markers)
    } catch {
      // A chart without markers is still worth showing.
    }

    this.chart.timeScale().fitContent()
    this.observeResize()
  }

  observeResize() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = new ResizeObserver((entries) => {
      if (!entries.length || !this.chart) return
      const { width, height } = entries[0].contentRect
      if (width <= 0) return
      this.chart.applyOptions(height > 0 ? { width, height } : { width })
    })
    this.resizeObserver.observe(this.chartTarget)
  }

  // theme:changed — refresh colours from the live CSS variables; the chart
  // instance and its series survive untouched.
  applyChartTheme() {
    if (!this.chart) return
    try {
      this.chart.applyOptions({
        layout: {
          background: { type: ColorType.Solid, color: CHART_COLORS.background },
          textColor: CHART_COLORS.text
        },
        grid: {
          vertLines: { color: CHART_COLORS.grid },
          horzLines: { color: CHART_COLORS.grid }
        },
        timeScale: { borderColor: CHART_COLORS.border },
        rightPriceScale: { borderColor: CHART_COLORS.border }
      })
    } catch (e) {
      console.warn("Failed to re-apply lab study chart theme:", e)
    }
  }

  teardownChart() {
    if (this.chart) {
      try {
        this.chart.remove()
      } catch {
        // Already disposed.
      }
    }
    this.chart = null
    this.series = null
    this.markers = null
  }
}
